import _ from 'underscore';
import uiIcon from '@shared/ui-icon';
import { T_ } from '@shared/translation';
import 'web-animations-js';
import template from './template.html';
import 'css/flux/modules/Callout.less';

const PLACEMENT = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  BOTTOM_LEFT: 'bottom-left',
  TOP_LEFT: 'top-left',
  TOP_RIGHT: 'top-right',
  BOTTOM_RIGHT: 'bottom-right',
};

export { PLACEMENT };

export default class Callout {
  /**
   * Constructs the callout.
   * @param  {Node}          targetEl             The target element in the DOM
   * to set as the trigger of the Callout.
   * @param  {Object}        [options={}]         Options to change the
   * behavior of the Callout. All these can also be set as data attributes of
   * the target element.
   * @param  {String}        options.title        The title of the Callout.
   * @param  {String}        options.content      The content of the Callout.
   * Can be a normal text or rich HTML content with options.richContent set to
   * true.
   * @param  {Boolean}       options.richContent  Set the Callout to have rich,
   * HTML markup in the content.
   * @param  {Boolean}       options.hoverable    Make the callout toggleable
   * by hovering. Defaults to false.
   * @param  {Boolean}       options.noClick      Disable click event of
   * the callout. Defaults to false.
   * @param  {Int}           hoverDelayOut        Time (in milliseconds) to
   * take for the callout to fade out when not on hover. Defaults to 0 (will
   * disable this feature).
   * @param  {String}        options.placement    The position of which the
   * callout is shown relative to the caller element. Defaults to bottom
   * placement.
   * @param  {Boolean}       options.closeButton  Enables the close button.
   * Defaults to true.
   * @param  {String[]}      options.extraClass   Extra classes to set on the
   * Callout. To use as data attributes just set as a space-separated string
   * instead.
   * @param {Boolean}        options.outsideClick Enables outside click
   * dismissal. Defaults to true.
   * @param {function}       options.onShow       Set a function to be called
   * each time the callout is shown. The function will be given a Callout
   * instance as the first argument.
   * @param {function}       options.onHide       Set a function to be called
   * each time the callout is hidden. The function will be given a Callout
   * instance as the first argument.
   * @return {Callout}                            The Callout instance.
   */
  constructor(targetEl, options = {}) {
    if (!targetEl) {
      throw new TypeError('Element is not a valid DOM element.');
    }

    if (!targetEl.id) {
      throw new TypeError('Element needs to have an ID.');
    }

    this.targetEl = targetEl;

    // Start parsing options.
    this.options = {};

    this.options.title = this._parseOption('title', options);
    this.options.content = this._parseOption('content', options, '');
    this.options.richContent = this._parseOption(
      'richContent', options, false, false);
    this.options.placement = this._parseOption(
      'placement', options, PLACEMENT.BOTTOM);
    this.options.hoverable = this._parseOption('hoverable', options, false);
    this.options.noClick = this._parseOption('noClick', options, false);
    this.options.hoverDelayOut = this._parseOption('hoverDelayOut', options, 0);
    this.options.closeButton = true;
    const closeButtonAttr = this.targetEl.getAttribute('data-closeButton');
    if (closeButtonAttr !== null) {
      this.options.closeButton = closeButtonAttr !== 'false';
    }
    if (options.closeButton !== undefined) {
      this.options.closeButton = options.closeButton;
    }
    if (Array.isArray(options.extraClass)) {
      // Convert array of classes to space-separated string.
      this.options.extraClass = options.extraClass.join(' ');
    } else if (typeof options.extraClass === 'string') {
      this.options.extraClass = options.extraClass;
    }
    this.options.extraClass = this._parseOption(
      'extraClass', this.options, '', false);

    this.options.outsideClick = this._parseOption(
      'outsideClick', options, true);

    this.options.onShow = this._parseOption(
      'onShow', options, () => {});

    this.options.onHide = this._parseOption(
      'onHide', options, () => {});
    // In order to detect closing callout from the outside we attach two
    // events (body and callout) when shown and use cancelCloseSignal to
    // signal from the callout click event if the click occurs in the callout
    // to cancel closing it.
    this.cancelCloseSignal = false;

    // Inject the callout before the </body>
    document.body.insertAdjacentHTML('beforeend', this._createFromTemplate({
      id: this.targetEl.id,
      options: this.options,
    }));

    // Get the injected callout DOM
    this.element = document.getElementById(`callout-${this.targetEl.id}`);
    this.element.isActive = false;
    if (this.options.closeButton) {
      this.element.closeButton = document
        .getElementById(`callout-${this.targetEl.id}-close`);
    }
    this.element.arrow = document
      .getElementById(`callout-${this.targetEl.id}-arrow`);

    // Add event listeners
    this.eventListeners = {
      elementHoverIn: () => {
        if (this.options.hoverDelayOut) {
          clearTimeout(this._delayOutTimeout);
        }
      },
      elementHoverOut: () => {
        if (this.options.hoverDelayOut) {
          this._delayOut(this.options.hoverDelayOut);
        }
      },
      targetElClick: () => this.toggle(),
      closeButtonClick: () => this.hide(),
      targetElHoverIn: () => {
        // Only trigger a show if the callout is not active/visible
        if (!this.element.isActive) {
          this.show();
        }

        // Only clear timeout if hoverable and has hoverDelayOut
        if (this.options.hoverable && this.options.hoverDelayOut) {
          clearTimeout(this._delayOutTimeout);
        }
      },
      targetElHoverOut: () => {
        // Only set timeout if hoverable and has hoverDelayOut otherwise hide
        // immediately.
        if (this.options.hoverable && this.options.hoverDelayOut) {
          this._delayOut(this.options.hoverDelayOut);
        } else {
          this.hide();
        }
      },
      viewportResize: () => this.recalculatePosition(),
      // Outside click listeners that interact with each other
      outsideClickBody: () => {
        if (!this.cancelCloseSignal) {
          this.hide();
        }
        this.cancelCloseSignal = false;
      },
      outsideClickCallout: () => {
        this.cancelCloseSignal = true;
      },
    };

    if (!this.options.noClick) {
      this.targetEl.addEventListener('click',
        this.eventListeners.targetElClick);
    }

    if (this.options.closeButton) {
      this.element.closeButton.addEventListener('click',
        this.eventListeners.closeButtonClick);
    }

    if (this.options.hoverable) {
      this.targetEl.addEventListener('mouseenter',
        this.eventListeners.targetElHoverIn);
      this.targetEl.addEventListener('mouseleave',
        this.eventListeners.targetElHoverOut);
      this.element.addEventListener('mouseenter',
        this.eventListeners.elementHoverIn);
      this.element.addEventListener('mouseleave',
        this.eventListeners.elementHoverOut);
    }

    // Set up animation with the Web Animation API
    this.animationPlayer = null;

    // Set the position of the callout for stylings to communicate with the
    // right CSS properties.
    this.setPlacement(this.options.placement);

    return this;
  }

  /**
   * Hide the callout after the given delay
   * @param  {Int}  delay  Time delay in milliseconds before
   *                       hiding the callout.
   */
  _delayOut(delay) {
    this._delayOutTimeout = setTimeout(() => {
      this.hide();
    }, delay);
  }

  /**
   * Parse the option from the options parameter passed, then the data
   * attribute set in the target element.
   * @param  {String}  name                  The name of the option.
   * @param  {Object}  options               Options Object from the
   *                                         constructor.
   * @param  {*}       [defaultValue=null]   The default value if parsing
   *                                         options and the data attribute
   *                                         fails.
   * @param  {Boolean} [dataAttributes=true] Check data attributes with the
   *                                         template of [data-{name}] for a
   *                                         fallback value if options don't
   *                                         have the value.
   * @return {*}                             The parsed option.
   */
  _parseOption(name, options, defaultValue = null, dataAttributes = true) {
    return options[name] ||
      (dataAttributes === true ? this.targetEl.getAttribute(`data-${name}`) :
        false) ||
      defaultValue;
  }

  /**
   * Creates the callout from the template.
   * @param  {Object} data An Object containing the data needed by the
   *                       template.
   * @return {String}      The compiled template in HTML in string.
   */
  _createFromTemplate(data) {
    const compiledTemplate = _.template(template);
    // Add uiIcon and translation's T_ in.
    data.uiIcon = uiIcon;
    data.T_ = T_;
    return compiledTemplate(data);
  }

  /**
   * Sets the placement information to the Callout so CSS can take over and
   * position things like where the arrow is pointing to.
   * @param {String} placement  The placement of the Callout relative to the
   *                            callout element. Defaults to
   *                            this.options.placement.
   */
  setPlacement(placement = this.options.placement) {
    this.element.setAttribute('data-placement', placement);
  }

  /**
   * Recalculates the position of the callout relative to the element.
   * @param  {String} placement The placement of the callout.
   * @return {Promise}          A promise resolving to when the position is set
   *                            on the next animation frame with the new top
   *                            and left position in an Object.
   */
  recalculatePosition(placement = this.options.placement) {
    // Set the placement in case this method is called with a placement override
    // (for example if the Callout is positioned at the bottom and it will
    // overflow from the bottom of the document).
    this.setPlacement(placement);
    // Get the positions and dimensions of the Callout, target element and the
    // document body. Always batch this so layout thrashing doesn't happen.
    const boundingClientRect = this.targetEl.getBoundingClientRect();
    const elementWidth = boundingClientRect.width;
    const elementHeight = boundingClientRect.height;
    const calloutBoundingClientRect = this.element.getBoundingClientRect();
    const calloutWidth = calloutBoundingClientRect.width;
    const calloutHeight = calloutBoundingClientRect.height;
    // IE is such a bad browser. document.documentElement is used for IE
    // compatibility.
    const bodyTop = (document.documentElement &&
      document.documentElement.scrollTop) || document.body.scrollTop;
    const bodyLeft = (document.documentElement &&
      document.documentElement.scrollLeft) || document.body.scrollLeft;
    const bodyWidth = document.body.clientWidth;
    const bodyHeight = document.body.clientHeight;

    // Now calculate the positions to be set to the Callout element.
    // Find the location of the target element relative to the document first.
    let top = boundingClientRect.top + bodyTop;
    let left = boundingClientRect.left + bodyLeft;

    if (placement === PLACEMENT.BOTTOM || placement === PLACEMENT.BOTTOM_LEFT ||
      placement === PLACEMENT.BOTTOM_RIGHT) {
      top = top + elementHeight + 20;
      // Recalculate the placement as a top placement if it overflows from
      // the bottom of the document. Disable this though if the new position
      // also overflows.
      if (top + calloutHeight > bodyHeight &&
        placement === this.options.placement) {
        return this.recalculatePosition(PLACEMENT.TOP);
      }
    } else if (placement === PLACEMENT.TOP ||
      placement === PLACEMENT.TOP_LEFT ||
      placement === PLACEMENT.TOP_RIGHT) {
      top = top - calloutHeight - 20;
      // Recalculate the placement as a bottom placement if it overflows from
      // the top of the document. Disable this though if the new position
      // also overflows.
      if (top < 0 && placement === this.options.placement) {
        return this.recalculatePosition(PLACEMENT.BOTTOM);
      }
    } else {
      // This calculation works for PLACEMENT.LEFT and PLACEMENT.RIGHT.
      top = Math.max(Math.min(
        top - (calloutHeight / 2),
        bodyHeight - calloutHeight - 20,
      ), 20);
    }

    if (placement === PLACEMENT.LEFT) {
      left = left - calloutWidth - 20;
      // Recalculate the placement as a bottom placement if it overflows from
      // the left of the document. Disable this though if the new position
      // also overflows.
      if (left < 0 && placement === this.options.placement) {
        return this.recalculatePosition(PLACEMENT.BOTTOM);
      }
    } else if (placement === PLACEMENT.RIGHT) {
      left = left + elementWidth + 20;
      // Recalculate the placement as a bottom placement if it overflows from
      // the right of the document. Disable this though if the new position
      // also overflows.
      if (left + calloutWidth > bodyWidth &&
        placement === this.options.placement) {
        return this.recalculatePosition(PLACEMENT.BOTTOM);
      }
    } else if (placement === PLACEMENT.BOTTOM_LEFT ||
      placement === PLACEMENT.TOP_LEFT) {
      // If the callout width is less than 500 change to PLACEMENT.BOTTOM
      // instead.
      if (calloutWidth < 500) {
        // TODO: This code still doesn't work properly because now the placement
        // jumps between BOTTOM to BOTTOM_LEFT on every viewport resize.
        // return this.recalculatePosition(PLACEMENT.BOTTOM);
      }
    } else if (placement === PLACEMENT.BOTTOM_RIGHT ||
      placement === PLACEMENT.TOP_RIGHT) {
      // Set the callout at the right side of the target element.
      left = Math.min(
        // Between setting it in the right side of the target element or setting
        // it at the end of the page
        left + elementWidth - calloutWidth,
        bodyWidth - calloutWidth - 20,
      );
    } else {
      // This calculation works for PLACEMENT.TOP and PLACEMENT.BOTTOM.
      left = Math.max(Math.min(
        // Between setting it in the middle of the target element or setting
        // it at the end of the page
        left + (elementWidth / 2) - (calloutWidth / 2),
        bodyWidth - calloutWidth - 20,
      ), 20);
    }

    // Now calculate the arrow position
    // Note that [x]_LEFT and [x]_RIGHT arrow positions are predefined
    // in CSS instead.
    let arrowTop = 0;
    let arrowLeft = 0;

    if (placement === PLACEMENT.TOP ||
      placement === PLACEMENT.BOTTOM) {
      arrowLeft = boundingClientRect.left + bodyLeft + (elementWidth / 2) -
        left;
    } else if (placement === PLACEMENT.LEFT || placement === PLACEMENT.RIGHT) {
      arrowTop = boundingClientRect.top + bodyTop + (elementHeight / 2) - top;
    }

    return new Promise((resolve) => {
      // Set the new positions on the next animation frame.
      const animate = () => {
        this.element.style.top = `${top}px`;
        this.element.style.left = `${left}px`;

        if (arrowTop) {
          this.element.arrow.style.top = `${arrowTop}px`;
        } else {
          this.element.arrow.style.top = null;
        }
        if (arrowLeft) {
          this.element.arrow.style.left = `${arrowLeft}px`;
        } else {
          this.element.arrow.style.left = null;
        }

        resolve({
          top,
          left,
        });
      };

      if ('requestAnimationFrame' in window) {
        window.requestAnimationFrame(animate);
      } else {
        animate();
      }
    });
  }

  /**
   * Sets up the callout opening and closing animation with the Web Animation
   * API
   * @param  {Boolean} playOpen  Sets the animation to play the opening
   *                             animation first. Defaults to true.
   */
  _setupAnimationAndPlay(playOpen = true) {
    this.animationPlayer = this.element.animate([{
      opacity: playOpen ? 0 : 1,
    }, {
      opacity: playOpen ? 1 : 0,
    }], {
      duration: 300,
      fill: 'forwards',
    });
  }

  /**
   * Toggles the callout to open or close.
   * @return {Promise} Resolves to the callout finished being shown or hidden
   *                   without any interruption, or rejects on interruption,
   *                   both returning the animation event.
   */
  toggle() {
    return this.element.isActive ? this.hide() : this.show();
  }

  /**
   * Shows the callout
   * @return {Promise} Resolves to the callout finished being shown without any
   *                   interruption, or rejects on interruption, both returning
   *                   the animation event.
   */
  show() {
    return new Promise((resolve, reject) => {
      this.element.classList.add('is-Callout-active');
      // Recalculate the position then show when it finishes.
      this.recalculatePosition().then(() => {
        this.element.isActive = true;
        // Animate the show transition with Web Animations API on an animation
        // frame.
        const animate = () => {
          // Animate using the existing player if there's any
          if (this.animationPlayer) {
            this.animationPlayer.playbackRate = 1;
            this.animationPlayer.play();
          } else {
            this._setupAnimationAndPlay();
          }

          this.animationPlayer.onfinish = (event) => {
            this.animationPlayer.onfinish = null;
            resolve(event);
          };
          this.animationPlayer.oncancel = (event) => {
            this.animationPlayer.oncancel = null;
            reject(event);
          };
        };

        if ('requestAnimationFrame' in window) {
          window.requestAnimationFrame(animate);
        } else {
          animate();
        }

        // Add the resize event when it's shown
        window.addEventListener('resize', this.eventListeners.viewportResize);

        // Add the outside click event when it's shown if the option is enabled.
        if (this.options.outsideClick) {
          this.element.addEventListener('click',
            this.eventListeners.outsideClickCallout);
          document.body.addEventListener('click',
            this.eventListeners.outsideClickBody);
        }

        this.options.onShow(this);
      });
    });
  }

  /**
   * Hides the callout.
   * @return {Promise} Resolves to the callout finished being hidden without any
   *                   interruption, or rejects on interruption, both returning
   *                   the animation event.
   */
  hide() {
    return new Promise((resolve, reject) => {
      this.element.isActive = false;
      // Animate the hide transition with Web Animations API on an animation
      // frame.
      const animate = () => {
        // Animate using the existing player if there's any
        if (this.animationPlayer) {
          this.animationPlayer.playbackRate = -1;
          this.animationPlayer.play();
        } else {
          this._setupAnimationAndPlay(false);
        }

        this.animationPlayer.onfinish = (event) => {
          this.element.classList.remove('is-Callout-active');
          this.animationPlayer.onfinish = null;
          resolve(event);
        };
        this.animationPlayer.oncancel = (event) => {
          this.animationPlayer.oncancel = null;
          reject(event);
        };
      };

      if ('requestAnimationFrame' in window) {
        window.requestAnimationFrame(animate);
      } else {
        animate();
      }

      // Remove the resize event
      window.removeEventListener('resize',
        this.recalculatePosition.bind(this));

      // Remove the outside click event when it's shown if the option is enabled
      if (this.options.outsideClick) {
        this.element.removeEventListener('click',
          this.eventListeners.outsideClickCallout);
        document.body.removeEventListener('click',
          this.eventListeners.outsideClick);
      }
      this.options.onHide(this);
    });
  }

  /**
   * Removes the callout entirely from the document.
   */
  remove() {
    document.body.removeChild(this.element);

    // Remove all event listeners related to the Callout.
    this.targetEl.removeEventListener('click',
      this.eventListeners.targetElClick);
    window.removeEventListener('resize',
      this.eventListeners.viewportResize);
    this.targetEl.removeEventListener('mouseenter',
      this.eventListeners.targetElHoverIn);
    this.targetEl.removeEventListener('mouseleave',
      this.eventListeners.targetElHoverOut);
  }
}
